import { Minus, Plus, Trash2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { useConfig, money, cleanTitle } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useSeason } from "@/lib/holiday";
import { GiftWrapBox } from "./GiftWrap";
import { Countdown } from "./Countdown";

const ShippingProgress = ({ subtotal }) => {
  const { data } = useConfig();
  const { t } = useI18n();
  const goal = data?.free_shipping_threshold || 50;
  const left = Math.max(0, goal - subtotal);
  return (
    <div data-testid="cart-shipping-progress" className="px-6 py-4 border-b border-white/5">
      <p className="text-xs text-white/70 mb-2">
        {left > 0 ? t("shipping_left", { amount: money(left) }) : <span className="text-gold font-semibold">{t("shipping_done")}</span>}
      </p>
      <div className="h-1 bg-white/10 overflow-hidden">
        <motion.div className="h-full bg-gold" initial={false} animate={{ width: `${Math.min(100, (subtotal / goal) * 100)}%` }} transition={{ type: "spring", stiffness: 90, damping: 18 }} />
      </div>
    </div>
  );
};

const Line = ({ item }) => {
  const { setQty, remove, setOpen } = useCart();
  return (
    <motion.div layout initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 60, height: 0 }} transition={{ duration: 0.4 }}
      data-testid={`cart-item-${item.variant_id}`} className="flex gap-4 py-5 border-b border-white/5">
      <Link to={`/products/${item.handle}`} onClick={() => setOpen(false)} className="h-24 w-20 bg-white shrink-0">
        <img src={item.image} alt={item.title} className="h-full w-full object-contain p-1" />
      </Link>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{item.brand}</p>
        <p className="text-sm leading-snug truncate">{cleanTitle(item.title)}</p>
        {item.variant_title && <p className="text-xs text-white/50">{item.variant_title}</p>}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center border border-white/15">
            <button data-testid={`cart-item-decrease-${item.variant_id}`} onClick={() => setQty(item.variant_id, item.quantity - 1)} className="h-8 w-8 flex items-center justify-center hover:text-gold"><Minus size={13} /></button>
            <span data-testid={`cart-item-qty-${item.variant_id}`} className="w-8 text-center text-sm">{item.quantity}</span>
            <button data-testid={`cart-item-increase-${item.variant_id}`} onClick={() => setQty(item.variant_id, item.quantity + 1)} className="h-8 w-8 flex items-center justify-center hover:text-gold"><Plus size={13} /></button>
          </div>
          <span className="text-sm font-semibold">{money(item.price * item.quantity)}</span>
        </div>
      </div>
      <button data-testid={`cart-item-remove-${item.variant_id}`} onClick={() => remove(item.variant_id)} className="text-white/40 hover:text-white self-start" aria-label="Remove"><Trash2 size={15} /></button>
    </motion.div>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, checkout, discount, redirecting, count, wrap } = useCart();
  const { data: cfg } = useConfig();
  const wrapOn = wrap.on && !!cfg?.gift_wrap;
  const { t } = useI18n();
  const { active, tierFor } = useSeason();
  const tier = active ? tierFor(count) : null;
  const code = tier ? tier.code : discount;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" data-lenis-prevent className="bg-[#0e0d0b] border-l border-white/10 w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/5 text-left">
          <SheetTitle className="font-display text-2xl text-white">{t("cart_title")}</SheetTitle>
          <SheetDescription className="text-white/50 text-xs">{items.length ? t("cart_count", { n: items.length }) : t("cart_empty")}</SheetDescription>
        </SheetHeader>
        {items.length > 0 && <ShippingProgress subtotal={subtotal} />}
        <div className="flex-1 overflow-y-auto px-6">
          <AnimatePresence initial={false}>{items.map((i) => <Line key={i.variant_id} item={i} />)}</AnimatePresence>
          {!items.length && (
            <div className="py-16 text-center">
              <p className="text-white/50 mb-6 text-sm">{t("cart_empty_text")}</p>
              <Link data-testid="cart-empty-shop-link" to="/shop" onClick={() => setOpen(false)} className="btn-gold inline-block px-8 py-4">{t("shop_now")}</Link>
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-3 max-h-[62vh] overflow-y-auto" data-lenis-prevent>
            <Countdown compact />
            <GiftWrapBox />
            {code && <p data-testid="cart-discount-note" className="text-xs text-gold">{t("cart_code_note", { code })}{tier && ` (-${tier.pct}%)`}</p>}
            <div className="flex justify-between text-base"><span>{t("subtotal")}</span><span data-testid="cart-subtotal" className="font-semibold">{money(subtotal + (wrapOn ? cfg.gift_wrap.price : 0))}</span></div>
            <p className="text-[11px] text-white/40">{t("taxes_note")}</p>
            <button data-testid="cart-checkout-button" disabled={redirecting} onClick={() => checkout(items, code, { gift_wrap: wrapOn, note: active ? wrap.message : undefined })} className="btn-gold btn-shine w-full h-14 flex items-center justify-center gap-2">
              <Lock size={14} /> {redirecting ? t("opening_checkout") : t("checkout")}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
